import axios from "axios";
import { notifyError } from "./notificationService";
import { obtenerToken, cerrarSesion, obtenerSesion, obtenerUsuario } from "./authService";

const api = axios.create({
    baseURL: process.env.REACT_APP_API_URL,
    timeout: 30000
});

api.interceptors.request.use((config) => {
    // Medir tiempo de inicio de la peticion
    config.metadata = { startTime: new Date() };

    const token = obtenerToken();
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }

    const sesion = obtenerSesion();
    if (sesion) {
        config.headers["X-Session-Id"] = sesion;
    }

    // Generar RequestId si no viene uno asignado
    if (!config.headers["X-Request-Id"]) {
        const idReq = typeof crypto !== "undefined" && crypto.randomUUID
            ? crypto.randomUUID()
            : "req_" + Date.now().toString(36) + Math.random().toString(36).substring(2, 7);
        config.headers["X-Request-Id"] = idReq;
    }

    return config;
});

api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (axios.isCancel(error) || error.code === "ERR_CANCELED" || error.name === "CanceledError") {
            return Promise.reject(error);
        }

        const estado = error.response?.status;
        const config = error.config || {};
        const tiempoRespuestaMs = config.metadata?.startTime
            ? Math.round(new Date() - config.metadata.startTime)
            : null;

        // Registro del error en la bitacora de errores si no fue registrado ya por el backend.
        try {
            const url = config.url || "";
            const backendCodigo = error.response?.data?.codigoError
                || error.response?.data?.extensions?.codigoError
                || null;
            const yaRegistradoEnBackend = error.response?.headers?.["x-error-logged"] === "true"
                || Boolean(backendCodigo);

            if (!url.includes("BitacoraError") && !yaRegistradoEnBackend) {
                const token = obtenerToken();
                const sesion = obtenerSesion();
                const reqId = error.response?.headers?.["x-request-id"]
                    || config.headers?.["X-Request-Id"]
                    || null;

                // Inferencia del modulo y operacion segun la ruta
                const rutaLimpia = url.replace(/^\//, "").split("?")[0];
                const partesRuta = rutaLimpia.split("/");
                const modulo = partesRuta[0] ? partesRuta[0].substring(0, 60) : null;
                const operacion = partesRuta[1] ? partesRuta[1].substring(0, 100) : (config.method || "").toUpperCase();

                const endpointCompleto = ((config.method || "").toUpperCase() + " " + url).substring(0, 100);

                fetch((process.env.REACT_APP_API_URL || "") + "/BitacoraError/Add", {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        ...(token ? { Authorization: `Bearer ${token}` } : {}),
                        ...(sesion ? { "X-Session-Id": sesion } : {}),
                    },
                    body: JSON.stringify({
                        nivel: estado >= 500 ? "ERROR" : (!estado ? "ERROR" : "WARNING"),
                        categoria: !estado ? "NETWORK" : "FRONTEND",
                        codigoError: (backendCodigo || (estado ? `HTTP_${estado}` : "NETWORK_ERROR")).substring(0, 60),
                        mensaje: (error.message || "Error en solicitud HTTP").substring(0, 400),
                        modulo: modulo,
                        operacion: operacion,
                        usuario: (obtenerUsuario() || "").substring(0, 60) || null,
                        sesionId: sesion && !isNaN(parseInt(sesion, 10)) ? parseInt(sesion, 10) : null,
                        requestId: reqId ? String(reqId).substring(0, 50) : null,
                        codigoHttp: estado || null,
                        endpoint: endpointCompleto,
                        tiempoRespuestaMs: tiempoRespuestaMs,
                        detalleTecnico: error.response?.data
                            ? JSON.stringify(error.response.data).slice(0, 2000)
                            : (error.stack ? String(error.stack).slice(0, 2000) : null),
                    }),
                }).catch(() => {});
            }
        } catch (e) { /* ignore */ }

        if (estado === 401 && window.location.pathname !== "/login") {
            cerrarSesion();
            window.location.replace("/login");
        }
        else if (estado === 403) {
            notifyError("No tiene permisos para ver o modificar esta información.");
        }
        else if (estado === 500) {
            notifyError("Ocurrió un error interno en el servidor.");
        }
        else if (!error.response) {
            notifyError("No se pudo conectar con el servidor.");
        }

        return Promise.reject(error);
    }
);

export default api;
